import { useFormContext } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Box,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoIcon from '@mui/icons-material/Info';
import FormToggleField from '../../../../components/formFields/FormToggleField';
import FormSelectField from '../../../../components/formFields/FormSelectField';
import { INTENT_TYPE_OPTIONS, ROWS_PER_PAGE_OPTIONS, NETSUITE_LABEL } from '../../../../constants/constants';
import AddButton from '../../../../components/buttons/AddButton';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import { useState } from 'react';
import TablePaginationLocalContainer from '../../../../components/Table/TablePaginationLocalContainer';
import { updateDeductionRecord } from '../../../../api/paymentsApi/deductionsApi';
import useSnackBar from '../../../../hooks/useSnackBar';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TruncatedText from '../../../../components/TruncatedText';
import { DeductionTypeMapping } from '../constants';
import { AxiosError } from 'axios';
import { ErrorData } from '../../../../types';
import theme from '../../../../theme';
import LaunchIcon from '@mui/icons-material/Launch';

const useStyles = makeStyles<{ isLongData?: boolean }>()((theme: Theme) => ({
  accordionRoot: {
    backgroundColor: 'transparent',
    borderRadius: '16px',
    // border: `1px solid ${theme.palette.secondary.dark}`,
    marginTop: theme.spacing(2),
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Poppins',
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Poppins',
    '& .MuiTypography-root': {
      fontWeight: 600,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },
    flexDirection: 'row-reverse'
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3), // reduced gap
    fontFamily: 'Poppins',
  },
  tableWrapper: {
    overflow: 'hidden',
    background: theme.palette.primary.light,
    borderRadius: '8px',
    fontFamily: 'Poppins',
  },
  tableTitle: {
    fontWeight: 600,
    fontSize: '15px',
    color: theme.palette.common.white,
    padding: theme.spacing(1.5, 2),
    fontFamily: 'Poppins',
  },
  tableContainer: {
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Poppins',
    // minHeight: isLongData ? '300px' : 'unset',
    // minHeight removed to prevent extra space
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },

  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Poppins',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: '150px',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Poppins',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      minWidth: '140px',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  labelText: {
    fontSize: '12px !important',
    fontFamily: 'Poppins !important',
    color: '#FFFFFFB3 !important',
    fontWeight: '200 !important',
    paddingTop: '2px !important',
  },
  columnAction: {
    minWidth: '80px !important',
    maxWidth: '80px !important',
  },
  columnAdd: {
    minWidth: '60px !important',
    maxWidth: '60px !important',
  },
  columnVendor: {
    minWidth: '220px !important',
    maxWidth: '220px !important',
  },
  deductionTextCell: {
    '& .MuiTypography-root': {
      fontFamily: 'Poppins !important',
    }
  }
}));

interface SettlementsDeductionsTableProps {
  disabled?: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
  isOnboardingReadOnly?: boolean;
  fields: Record<'id', string>[];
  handleMenuOptionClick: (option: string, index: number, fieldIndex: number) => void;
  deductionTotal: {
    total: number;
    liens: number;
    liensWithheld: number;
    advances: number;
    advancesWithheld: number;
    serviceProviders: number;
    serviceProvidersWithheld: number;
    totalLien: number;
    totalAdvance: number;
    totalServiceProvider: number;
  };
  onSuccess?: () => void;
  onCancel: () => void;
  intentType?: 'Pay' | 'Withhold';
  // onDeductionDispositionChange?: (payload: {
  //   deductionId: number;
  //   disposition?: string;
  //   included?: boolean;
  // }) => void;
  settlementId?: number;
}

const SettlementsDeductionsTable = ({
  disabled,
  canEdit,
  canCreate,
  isOnboardingReadOnly,
  fields,
  handleMenuOptionClick,
  deductionTotal,
  onCancel,
  intentType,
  onSuccess,
  settlementId
}: SettlementsDeductionsTableProps) => {
  const { watch, setValue } = useFormContext();
  const { setAlert } = useSnackBar();

  const deductionsFields = watch('deduction');
  const isLongData = deductionsFields?.length > 3;
  const styles = useStyles({ isLongData });
  const [liensPage, setLiensPage] = useState(0);
  const [liensRowsPerPage, setLiensRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const [serviceProviderPage, setServiceProviderPage] = useState(0);
  const [serviceProviderRowsPerPage, setServiceProviderRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const [expensesPage, setExpensesPage] = useState(0);
  const [expensesRowsPerPage, setExpensesRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);


  const renderTable = (title: string) => {
    const isLien = title === 'Lien';
    const isServiceProvider = title === 'Service Provider';
    const page = isLien ? liensPage : isServiceProvider ? serviceProviderPage : expensesPage;
    const rowsPerPage = isLien ? liensRowsPerPage : isServiceProvider ? serviceProviderRowsPerPage : expensesRowsPerPage;
    const setPage = isLien ? setLiensPage : isServiceProvider ? setServiceProviderPage : setExpensesPage;
    const setRowsPerPage = isLien ? setLiensRowsPerPage : isServiceProvider ? setServiceProviderRowsPerPage : setExpensesRowsPerPage;

    // Filter deductions by type, but always keep the original index from fields
    const filtered = fields
      .map((field, idx) => ({ field, idx }))
      .filter(({ idx }) => deductionsFields?.[idx]?.type === DeductionTypeMapping[title]);

    const paginated = filtered.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );


    return (
      <Box className={styles.classes.tableWrapper}>
        <Box display="flex" alignItems="center" width="99%" justifyContent="space-between">
          <Typography className={styles.classes.tableTitle}>{title === 'Lien' ? 'Liens' : title === 'Service Provider' ? 'Service Providers' : 'Expenses'}</Typography>
          <Typography>
            {title === 'Lien' ? numberToUSDCurrency(deductionTotal.totalLien) : title === 'Service Provider' ? numberToUSDCurrency(deductionTotal.totalServiceProvider) : numberToUSDCurrency(deductionTotal.totalAdvance)}
          </Typography>
        </Box>
        <TableContainer className={styles.classes.tableContainer} sx={{ width: '99%' }} component={Paper} elevation={0}>
          <Table sx={{
            minWidth: '430px',
            borderCollapse: 'collapse',
          }} >
            <TableHead className={styles.classes.tableHeaderCell}>
              <TableRow>
                <TableCell key={title === 'Lien' || title === 'Service Provider' ? 'Vendor' : 'Description'} className={styles.classes.columnVendor}>{title === 'Lien' || title === 'Service Provider' ? 'Vendor' : 'Description'}</TableCell>
                <TableCell key={'Amount'}>{'Amount'}</TableCell>
                <TableCell key={'Intent'}>{'Intent'}</TableCell>
                <TableCell key={'PaymentMethod'}>{'Payment Method'}</TableCell>
                {intentType !== 'Withhold' && <TableCell key={'Add'} className={styles.classes.columnAdd}>{'Add'}</TableCell>}
                <TableCell key={'Action'} className={styles.classes.columnAction}>{'Action'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={clsx(styles.classes.tableBodyCell)}>
              {paginated.length > 0 ? (
                paginated.map(({ field, idx }) => (
                  <TableRow
                    key={`${title}-${field.id}-${idx}`}
                    sx={{
                      opacity: watch(`deduction.${idx}.isAdded`) ? 1 : 0.5,
                      '& .MuiTableCell-root': {
                        color: watch(`deduction.${idx}.isAdded`) ? 'common.white' : 'text.disabled',
                      }
                    }}
                  >
                    <TableCell className={clsx(styles.classes.deductionTextCell, styles.classes.columnVendor)}>
                      <Box display="flex" alignItems="center" gap="8px" overflow="hidden">
                        <TruncatedText
                          text={title === 'Lien' || title === 'Service Provider' ? (deductionsFields[idx]?.vendorName || '-') : (deductionsFields[idx]?.description || '-')}
                          maxLength={30}
                          disabled={!watch(`deduction.${idx}.isAdded`)}
                        />
                        {(title === 'Lien' || title === 'Service Provider') && deductionsFields[idx]?.accounting?.isSynced && deductionsFields[idx]?.accounting?.link != null && (
                          <Tooltip
                            title={`View Vendor in ${NETSUITE_LABEL}`}
                            arrow
                            componentsProps={{
                              tooltip: {
                                sx: (theme) => ({
                                  backgroundColor: theme.palette.common.white,
                                  color: theme.palette.primary.light,
                                  borderRadius: '5px',
                                  boxShadow: '3px',
                                  fontFamily: 'Inter',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  padding: '5px',
                                  maxWidth: '140px',
                                }),
                              },
                            }}
                          >
                            <LaunchIcon
                              sx={{
                                cursor: 'pointer',
                                color: 'common.white',
                                fontSize: '20px',
                                flexShrink: 0,
                              }}
                              onClick={() => window.open(deductionsFields[idx]?.accounting?.link || '', '_blank')}
                            />
                          </Tooltip>
                        )}
                        {(title === 'Lien' || title === 'Service Provider') && deductionsFields[idx]?.accounting != null && (
                          <Box
                            sx={{
                              backgroundColor: deductionsFields[idx]?.accounting?.isSynced ? 'success.main' : 'warning.main',
                              color: 'white',
                              fontSize: '10px',
                              padding: '2px 6px',
                              borderRadius: '8px',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}
                          >
                            {deductionsFields[idx]?.accounting?.isSynced ? 'Synced' : 'Unsynced'}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display='flex' alignItems='center' justifyContent='flex-start' gap={1}>
                        {(() => {
                          const preAmount = parseFloat(deductionsFields[idx]?.preSettlementAmount?.replace(/[$,]/g, '') || '0');
                          const currentAmount = parseFloat(deductionsFields[idx]?.amount?.replace(/[$,]/g, '') || '0');
                          const savings = preAmount - currentAmount;

                          return (
                            <>
                              <Box sx={{ color: savings > 0 ? theme.palette.success.main : 'inherit' }} >
                                {deductionsFields[idx]?.amount || '-'}
                              </Box>
                              {savings > 0 && (
                                <Tooltip
                                  title={`Billed Amount: ${deductionsFields[idx]?.preSettlementAmount}`}
                                  arrow
                                  componentsProps={{
                                    tooltip: {
                                      sx: (theme) => ({
                                        backgroundColor: theme.palette.common.white,
                                        color: theme.palette.primary.light,
                                        borderRadius: '5px',
                                        boxShadow: '3px',
                                        fontFamily: 'Inter',
                                        fontSize: '13px',
                                        fontWeight: 500,
                                        padding: '5px',
                                        maxWidth: '300px',
                                      }),
                                    },
                                  }}
                                >
                                  <InfoIcon
                                    sx={{
                                      fontSize: '16px',
                                      color: theme.palette.success.main,
                                      cursor: watch(`deduction.${idx}.isAdded`) ? 'pointer' : 'default',
                                      paddingBottom: '2px',
                                      opacity: watch(`deduction.${idx}.isAdded`) ? 1 : 0.5,
                                      pointerEvents: watch(`deduction.${idx}.isAdded`) ? 'auto' : 'none',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </>
                          );
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {/* {title === 'Lien' ? */}
                      <FormSelectField
                        name={`deduction.${idx}.disposition`}
                        label="Intent"
                        options={INTENT_TYPE_OPTIONS}
                        width="100%"
                        disabled={!watch(`deduction.${idx}.isAdded`) || !canEdit}
                        customOnChange={(val) => {
                          const oldDisposition = watch(`deduction.${idx}.disposition`);
                          const oldPaymentMethod = watch(`deduction.${idx}.paymentMethod`);

                          const deductionRow = deductionsFields[idx] as Record<string, unknown>;
                          const deductionType = deductionRow?.type as string | undefined;

                          const isVendorType = deductionType === 'Lien' || deductionType === 'SERVICE_PROVIDER';
                          const isAdvanceType = deductionType === 'Advance';

                          const applyDispositionChange = async () => {
                            let nextPaymentMethod = oldPaymentMethod;

                            if (isVendorType) {
                              if (val === 'External') {
                                nextPaymentMethod = '';
                              }
                            } else if (isAdvanceType) {
                              if (val === 'External') {
                                nextPaymentMethod = '';
                              } else if (val === 'Pay' || val === 'Withhold') {
                                nextPaymentMethod = 'V-Firm';
                              }
                            } else if (val === 'Pay' || val === 'Withhold') {
                              nextPaymentMethod = 'V-Firm';
                            }

                            setValue(`deduction.${idx}.disposition`, val, {
                              shouldValidate: false,
                            });

                            setValue(`deduction.${idx}.paymentMethod`, nextPaymentMethod, {
                              shouldValidate: false,
                            });

                            return nextPaymentMethod;
                          };

                          applyDispositionChange()
                            .then((nextPaymentMethod) =>
                              updateDeductionRecord(deductionsFields[idx]?.deductionsId, {
                                disposition: val,
                                paymentMethod: nextPaymentMethod === 'V-Firm' ? 'V' : nextPaymentMethod,
                              }),
                            )
                            .then((res) => {
                              if (!res.data.error) {
                                onSuccess && onSuccess();

                                setAlert((prev) => ({
                                  ...prev,
                                  message: res.data.message || 'Deduction updated successfully.',
                                  type: 'success',
                                }));
                              }
                            })
                            .catch((error: AxiosError<ErrorData>) => {
                              setValue(`deduction.${idx}.disposition`, oldDisposition, {
                                shouldValidate: false,
                              });

                              setValue(`deduction.${idx}.paymentMethod`, oldPaymentMethod, {
                                shouldValidate: false,
                              });

                              setAlert((prev) => ({
                                ...prev,
                                message: error.response?.data.message || 'Failed to update deduction',
                                type: 'error',
                              }));
                            })
                            .finally(() => {
                              onCancel && onCancel();
                            });
                        }}
                      />
                      {/* : (deductionsFields[idx]?.disposition || '-')} */}
                    </TableCell>
                    <TableCell>
                      {deductionsFields[idx]?.paymentMethod || '-'}
                    </TableCell>
                    {intentType !== 'Withhold' && <TableCell className={styles.classes.columnAdd}>
                      {/* <FormToggleField
                        name={`deduction.${idx}.isAdded`}
                        customHandleChange={handleChangeAddToggle}
                        disabled={disabled}
                      /> */}
                      <FormToggleField
                        name={`deduction.${idx}.isAdded`}
                        customHandleChange={(checked: boolean) => {
                          const included = Boolean(checked);
                          const oldValue = !included;

                          updateDeductionRecord(deductionsFields[idx]?.deductionsId, {
                            ...(included
                              ? { includeToSettlement: settlementId }
                              : { excludeFromSettlement: settlementId }),
                          })
                            .then((res) => {
                              if (!res.data.error) {
                                onSuccess && onSuccess();

                                setAlert((prev) => ({
                                  ...prev,
                                  message: res.data.message || 'Deduction updated successfully.',
                                  type: 'success',
                                }));

                                // onDeductionDispositionChange?.({
                                //   deductionId: Number(deductionsFields[idx]?.id),
                                //   included,
                                // });
                              }
                            })
                            .catch((error: AxiosError<ErrorData>) => {
                              setValue(`deduction.${idx}.isAdded`, oldValue, {
                                shouldValidate: false,
                              });

                              setAlert((prev) => ({
                                ...prev,
                                message: error.response?.data.message || 'Failed to update deduction',
                                type: 'error',
                              }));
                            })
                            .finally(() => {
                              onCancel && onCancel();
                            });
                        }}
                        disabled={disabled}
                      />
                    </TableCell>}
                    <TableCell className={styles.classes.columnAction}>
                      <>
                        <Box display='flex' alignItems='center' justifyContent='flex-start' gap={4}>
                          <Box>
                            <Tooltip
                              title={
                                watch(`deduction.${idx}.isAdded`) && canEdit
                                  ? 'Edit'
                                  : (isOnboardingReadOnly ? 'This is disabled. See the details page for more information.' : 'Edit')
                              }
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '140px',
                                  }),
                                },
                              }}
                            >
                              <EditIcon
                                sx={{
                                  cursor: watch(`deduction.${idx}.isAdded`) && canEdit ? 'pointer' : 'not-allowed',
                                  color: 'common.white',
                                  opacity: watch(`deduction.${idx}.isAdded`) && canEdit ? 1 : 0.5,
                                  pointerEvents: watch(`deduction.${idx}.isAdded`) && canEdit ? 'auto' : 'none',
                                }}
                                onClick={watch(`deduction.${idx}.isAdded`) && canEdit
                                  ? () => handleMenuOptionClick('Edit', Number(deductionsFields[idx].id), idx)
                                  : undefined
                                }
                                fontSize='small'
                              />
                            </Tooltip>
                          </Box>
                          <Box>
                            <Tooltip
                              title="Download Invoice"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '140px',
                                  }),
                                },
                              }}
                            >
                              <FileDownloadIcon
                                sx={{
                                  cursor: watch(`deduction.${idx}.isAdded`) ? 'pointer' : 'not-allowed',
                                  color: 'common.white',
                                  marginLeft: '12px',
                                  opacity: watch(`deduction.${idx}.isAdded`) ? 1 : 0.5,
                                  pointerEvents: watch(`deduction.${idx}.isAdded`) ? 'auto' : 'none',
                                }}
                                onClick={watch(`deduction.${idx}.isAdded`)
                                  ? () => handleMenuOptionClick('Download Invoice', Number(deductionsFields[idx].id), idx)
                                  : undefined
                                }
                                fontSize='small'
                              />
                            </Tooltip>
                          </Box>
                        </Box>
                      </>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={intentType !== 'Withhold' ? 6 : 5} align="center" sx={{ textAlign: 'center !important' }}>
                    <Typography variant="body2" color="textSecondary">
                      No {(title === 'Lien' ? 'Lien' : title === 'Service Provider' ? 'Service Provider' : 'Expense')} records found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePaginationLocalContainer
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value as string, ROWS_PER_PAGE_OPTIONS[0]));
            setPage(0);
          }}
          totalPages={Math.ceil(filtered.length / rowsPerPage)}
        />
      </Box>
    );
  };

  return (
    <Accordion defaultExpanded className={styles.classes.accordionRoot}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
        className={styles.classes.accordionSummary}
      >
        <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap="8px">
            <Typography>Deductions</Typography>
            <Typography className={styles.classes.labelText}
            >
              Liens, Service Providers & Expenses
            </Typography>
          </Box>
          <Typography    >
            {numberToUSDCurrency(deductionTotal.total)}
          </Typography>
        </Box>

      </AccordionSummary>

      <AccordionDetails className={styles.classes.accordionDetails}>
        {/* {intentType !== 'Withhold' && */}
        {(canCreate || isOnboardingReadOnly) && (
          <Box display="flex">
            {isOnboardingReadOnly ? (
              <Tooltip
                title="This is disabled. See the details page for more information."
                arrow
              >
                <Box component="span" sx={{ display: 'inline-flex' }}>
                  <AddButton
                    buttonText="Create deduction"
                    path="/payments/create-deduction"
                    disabled
                  // disabled={entityData?.status === 'Paid'}
                  />
                </Box>
              </Tooltip>
            ) : (
              <AddButton
                buttonText="Create deduction"
                path="/payments/create-deduction"
              // disabled={entityData?.status === 'Paid'}
              />
            )}
          </Box>
        )}
        {/* } */}
        {renderTable('Lien')}
        {renderTable('Service Provider')}
        {renderTable('Advance')}
      </AccordionDetails>
    </Accordion>
  );
};

export default SettlementsDeductionsTable;